// src/ConsumerApp/Screens/ProductsList/components/TabletFiltersModal.js

import React, { useState, useEffect, useMemo } from 'react';
import X from '../../../../assets/icons/X.svg';

export default function TabletFiltersModal({
  visible = false,
  onClose,
  filterConfig = [],
  appliedFilters = [],
  onApply,
}) {
  const [draftFilters, setDraftFilters] = useState([]);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (visible) {
      setDraftFilters([...appliedFilters]);
      if (filterConfig.length > 0 && !activeCategoryId) {
        setActiveCategoryId(filterConfig[0].id);
      }
    }
  }, [visible, appliedFilters, filterConfig, activeCategoryId]);

  const activeCategory = useMemo(() => {
    return (
      filterConfig.find((c) => c.id === activeCategoryId) ||
      (filterConfig.length > 0 ? filterConfig[0] : null)
    );
  }, [activeCategoryId, filterConfig]);

  if (!visible) return null;

  const handleToggleOption = (category, option) => {
    setDraftFilters((prev) => {
      const exists = prev.find((f) => f.optionId === option.id);
      if (exists) {
        return prev.filter((f) => f.optionId !== option.id);
      }
      return [
        ...prev,
        {
          categoryId: category.id,
          categoryLabel: category.label,
          optionId: option.id,
          label: option.label,
        },
      ];
    });
  };

  const handleClear = () => setDraftFilters([]);

  const handleApplyCommit = () => {
    onApply(draftFilters);
    onClose();
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.headerRow}>
          <div>
            <h3 style={styles.title}>Filters</h3>
            <span style={styles.subtitle}>
              {draftFilters.length} options selected
            </span>
          </div>
          <button type="button" style={styles.closeBtn} onClick={onClose}>
            <img src={X} alt="close" style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Search Bar */}
        <div style={styles.searchRow}>
          <input
            type="text"
            style={styles.modalSearchInput}
            placeholder="Search across options..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* 2-Column Split: Categories on left, Options on right */}
        <div style={styles.splitBody}>
          <div style={styles.sidebar}>
            {filterConfig.map((cat) => {
              const isSelected = activeCategoryId === cat.id;
              const selectedCount = draftFilters.filter(
                (f) => f.categoryId === cat.id
              ).length;

              return (
                <div
                  key={cat.id}
                  style={{
                    ...styles.catTab,
                    ...(isSelected ? styles.catTabSelected : {}),
                  }}
                  onClick={() => setActiveCategoryId(cat.id)}
                >
                  <span style={styles.catTabTitle}>{cat.label}</span>
                  {selectedCount > 0 && (
                    <span style={styles.catTabBadge}>{selectedCount}</span>
                  )}
                </div>
              );
            })}
          </div>

          <div style={styles.optionsList}>
            {(activeCategory?.options || [])
              .filter((opt) =>
                opt.label.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((option) => {
                const isChecked = draftFilters.some(
                  (f) => f.optionId === option.id
                );

                return (
                  <label key={option.id} style={styles.optionRow}>
                    <input
                      type="checkbox"
                      style={styles.realCheckbox}
                      checked={isChecked}
                      onChange={() =>
                        handleToggleOption(activeCategory, option)
                      }
                    />
                    <span
                      style={{
                        ...styles.customBox,
                        ...(isChecked ? styles.customBoxChecked : {}),
                      }}
                    >
                      {isChecked && (
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#FFFFFF"
                          strokeWidth="3.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </span>
                    <span style={styles.optionText}>{option.label}</span>
                  </label>
                );
              })}
          </div>
        </div>

        {/* Footer Actions */}
        <div style={styles.footerRow}>
          <button type="button" style={styles.clearBtn} onClick={handleClear}>
            Clear Filter
          </button>
          <button
            type="button"
            style={styles.applyBtn}
            onClick={handleApplyCommit}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    zIndex: 999999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(3px)',
    padding: 16,
  },
  modalCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    width: '100%',
    maxWidth: 580,
    height: '75vh',
    maxHeight: 620,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #F1F5F9',
  },
  title: {
    margin: 0,
    fontSize: 17,
    fontWeight: '800',
    color: '#2B2A29',
  },
  subtitle: {
    fontSize: 12,
    color: '#707B8C',
    marginTop: 2,
    display: 'block',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
  },
  searchRow: {
    padding: '10px 20px',
    borderBottom: '1px solid #F1F5F9',
  },
  modalSearchInput: {
    width: '100%',
    height: 36,
    borderRadius: 8,
    border: '1px solid #CBD5E1',
    padding: '0 12px',
    fontSize: 13,
    color: '#2B2A29',
    outline: 'none',
    boxSizing: 'border-box',
    backgroundColor: '#F8FAFC',
  },
  splitBody: {
    display: 'grid',
    gridTemplateColumns: '180px 1fr',
    flex: 1,
    overflow: 'hidden',
  },
  sidebar: {
    backgroundColor: '#F8FAFC',
    borderRight: '1px solid #E2E8F0',
    overflowY: 'auto',
  },
  catTab: {
    padding: '12px 16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #F1F5F9',
  },
  catTabSelected: {
    backgroundColor: '#FFFFFF',
    borderLeft: '3px solid #E9001E',
  },
  catTabTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#2B2A29',
  },
  catTabBadge: {
    fontSize: 10.5,
    fontWeight: '700',
    color: '#FFFFFF',
    backgroundColor: '#E9001E',
    padding: '1px 5px',
    borderRadius: 999,
  },
  optionsList: {
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    overflowY: 'auto',
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
    userSelect: 'none',
  },
  realCheckbox: {
    display: 'none',
  },
  customBox: {
    width: 17,
    height: 17,
    borderRadius: 4,
    border: '1.5px solid #CBD5E1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    flexShrink: 0,
  },
  customBoxChecked: {
    borderColor: '#E9001E',
    backgroundColor: '#E9001E',
  },
  optionText: {
    fontSize: 13.5,
    color: '#2B2A29',
  },
  footerRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12,
    padding: '14px 20px',
    borderTop: '1px solid #E2E8F0',
    backgroundColor: '#FFFFFF',
  },
  clearBtn: {
    padding: '9px 18px',
    borderRadius: 8,
    border: '1px solid #CBD5E1',
    backgroundColor: '#FFFFFF',
    color: '#2B2A29',
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer',
  },
  applyBtn: {
    padding: '9px 24px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '700',
    cursor: 'pointer',
  },
};